import { useState } from "react";
import { toast } from "sonner";
import { EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// v53 — super admin "Hide" next to a creator's name on Creator Intel. Uses the
// existing site-wide hide (hidden_creators); undo via the toast or Hidden Creators.
export function HideCreatorButton({ id, name, onHidden, onUnhidden, label }) {
  const [open, setOpen] = useState(false);
  const undo = async () => {
    onUnhidden?.(id);
    const t = toast.loading(`Bringing ${name} back…`);
    try { await api.delete(`/admin/hidden-creators/${encodeURIComponent(id)}`); toast.success(`${name} is back`, { id: t }); }
    catch { onHidden?.(id); toast.error("Could not undo — use Admin → Hidden Creators", { id: t }); }
  };
  const hide = async () => {
    try {
      await api.post("/admin/hidden-creators", { channel_id: id });
      onHidden?.(id);
      toast(`Hidden · ${name}`, { action: { label: "Undo", onClick: undo }, duration: 10000 });
    } catch { toast.error("Could not hide this creator"); }
  };
  return (
    <>
      {label ? (
        <button type="button" data-testid="profile-hide-btn" title="Hide creator everywhere" aria-label="Hide creator"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          className="text-xs px-3 py-1.5 rounded-md border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 hover:border-rose-400 transition-colors duration-150 flex items-center gap-1.5">
          <EyeOff size={12} /> {label}
        </button>
      ) : (
        <button type="button" data-testid={`hide-creator-${id}`} title="Hide creator everywhere" aria-label="Hide creator"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          className="p-0.5 rounded text-rose-400 opacity-40 hover:opacity-100 hover:bg-rose-500/10 transition-opacity shrink-0">
          <EyeOff size={12} />
        </button>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="hide-creator-dialog" onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Hide {name} everywhere on KOL Radar?</AlertDialogTitle>
            <AlertDialogDescription>He disappears from every list, profile and count for everyone, and manual onboarding refuses him. You can undo this or unhide him from Admin → Hidden Creators.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hide-creator-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="hide-creator-confirm" onClick={hide} className="bg-rose-600 text-white hover:bg-rose-500">Hide</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
